import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './Payline.css';
import ExpiryInput from './ExpiryInput';
import CardNumberInput from './CardNumberInput';
import CvxInput from './CvxInput';
import WaitingBlock from '../WaitingBlock';
import { findTokenizer } from './utils';
import { getClientCapabilities } from '../../utils';

const Payline = React.forwardRef(
    (
        {
            config = {},
            token,
            lyriaUrl,
            expiryAsText = false,
            onPaymentResult,
            onPaymentStarted,
            hidePayButton = false,
            savePaymentMethod = false,
            loader,
            operation = '',
            savedPaymentMethods = []
        },
        ref
    ) => {
        const { t } = useTranslation();
        const [attempts, setAttempts] = React.useState(0);
        // Display behavior states
        const [isIframeShown, showIframe] = React.useState(false);
        const [isFormVisible, showForm] = React.useState(true);
        const [isCardFormVisible, showCardForm] = React.useState(false);
        const [isSpinnerVisible, showSpinner] = React.useState(false);
        const [isReadOnly, setReadOnly] = React.useState(false);
        const [submitTried, setSubmitTried] = React.useState(false);
        const iframe3ds = React.useRef(null);
        const iframe3dsmethod = React.useRef(null);
        const [savePaymentMethodValue, setSavePaymentMethodValue] = React.useState(false);
        const [selectedPaymentMethod, setSelectedPaymentMethod] = React.useState(null);

        // Payment method fields states
        const [card, setCard] = React.useState({
            value: '',
            isValid: false,
            cardType: null
        });
        const [expiry, setExpiry] = React.useState({
            value: {},
            isValid: false
        });
        const [cvc, setCvc] = React.useState({
            value: '',
            isValid: false
        });

        const handleSuccess = () => {
            onPaymentResult({ success: true });
            showForm(false);
            showIframe(false);
            showSpinner(false);
        };

        const handleError = response => {
            setAttempts(attempts => attempts + 1);
            showSpinner(false);
            setSubmitTried(false);
            setReadOnly(false);
            showForm(true);
            showIframe(false);
            onPaymentResult({ success: false, data: response });
        };

        const postMessageHandler = message => {
            if (message.origin === lyriaUrl) {
                if (message.data.status === 'success') {
                    handleSuccess();
                } else if (message.data.status === 'error') {
                    handleError(message.data.data);
                } else if (message.data.status === 'threedsmethod') {
                    verifyEnrollment(message.data);
                }
            }
        };

        React.useEffect(() => {
            window.addEventListener('message', postMessageHandler);
            return () =>
                window.removeEventListener('message', postMessageHandler);
        });

        React.useEffect(() => {
            if (savedPaymentMethods.length > 0) {
                setSelectedPaymentMethod(savedPaymentMethods[0].uuid);
            }
        }, [savedPaymentMethods]);

        const verifyEnrollment = verifyParameters => {
            showForm(false);
            let iframe;
            if (verifyParameters.code === '03100') {
                iframe = iframe3dsmethod.current;
                showSpinner(true);
                showIframe(false);
            } else {
                iframe = iframe3ds.current;
                showIframe(true);
            }

            const iframeContent = iframe.contentDocument;
            let form = iframeContent.createElement('form');
            form.setAttribute('method', verifyParameters.method);
            form.setAttribute('action', verifyParameters.url);
            let charsetField = iframeContent.createElement('input');
            charsetField.setAttribute('name', '_charset_');
            charsetField.setAttribute('type', 'hidden');
            charsetField.setAttribute('value', 'UTF-8');
            form.appendChild(charsetField);
            Object.keys(verifyParameters.data).forEach(function(key) {
                let hiddenField = iframeContent.createElement('input');
                hiddenField.setAttribute('name', key);
                hiddenField.setAttribute('type', 'hidden');
                hiddenField.setAttribute('value', verifyParameters.data[key]);
                form.appendChild(hiddenField);
            });
            iframeContent.documentElement.appendChild(form);
            form.submit();
            if (verifyParameters.code !== '03100') {
                showSpinner(false);
                iframe.style.display = 'block';
            }
        };

        const submitForm = () => {
            setSubmitTried(true);

            if (!(card.isValid && expiry.isValid && cvc.isValid)) {
                return false;
            }

            if (card.cardType === null) {
                return false;
            }

            const tokenizer = findTokenizer(
                card.cardType.type,
                config.tokenizers
            );

            if (tokenizer === null) {
                return false;
            }

            setReadOnly(true);
            showForm(false);
            showSpinner(true);
            onPaymentStarted();

            axios({
                method: tokenizer.method,
                url: tokenizer.url,
                headers: {
                    'Content-Type': tokenizer.content_type
                },
                data: tokenizer.data
                    .replace('__card__', card.value.replace(/\s/g, ''))
                    .replace(
                        '__expiry__',
                        `${String(expiry.value.month).padStart(2, '0')}${String(
                            expiry.value.year
                        ).substr(-2)}`
                    )
                    .replace('__cvx__', cvc.value)
            }).then(function({ data }) {
                if (operation === 'credit') {
                    axios({
                        method: 'post',
                        url: lyriaUrl + '/payline/save_tokenized_card_data',
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        data: {
                            request_token: token,
                            psp: tokenizer.id,
                            data
                        }
                    })
                        .then(function({data}) {
                            showSpinner(false);
                            if (data.status === 'success') {
                                handleSuccess();
                            } else if (data.status === 'error') {
                                handleError(data.data);
                            }
                        })
                        .catch(function(error) {
                            console.info(error);
                        });
                } else {
                    axios({
                        method: 'post',
                        url: lyriaUrl + '/payline/process',
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        data: {
                            client_capabilities: getClientCapabilities(),
                            request_token: token,
                            psp: tokenizer.id,
                            save_payment_method: savePaymentMethodValue,
                            data
                        }
                    })
                        .then(function({ data }) {
                            showSpinner(false);
                            if (data.status === 'V') {
                                handleSuccess();
                            } else if (data.status === 'A') {
                                handleError(data.data);
                            }
                        })
                        .catch(function(error) {
                            if (
                                error.response.data.status === 'error' &&
                            error.response.data.data.code ===
                                'verify_enrollment'
                            ) {
                                verifyEnrollment(error.response.data.data.action);
                            } else console.info(error);
                        });
                }
            });

            return true;
        };

        const submitFormForPayingWithSavedCard = () => {
            setSubmitTried(true);

            if (!cvc.isValid) {
                return false;
            }

            setReadOnly(true);
            showForm(false);
            showSpinner(true);

            // selectedPaymentMethod is the uuid of the selected card
            const selectedCard = savedPaymentMethods.find(element => element.uuid === selectedPaymentMethod);

            axios({
                method: 'post',
                url: lyriaUrl + '/payline/process',
                headers: {
                    'Content-Type': 'application/json'
                },
                data: {
                    client_capabilities: getClientCapabilities(),
                    request_token: token,
                    psp: selectedCard.psp,
                    data: '',
                    cvx: cvc.value,
                    saved_payment_method: selectedPaymentMethod
                }
            })
                .then(function({ data }) {
                    showSpinner(false);
                    if (data.status === 'V') {
                        handleSuccess();
                    } else if (data.status === 'A') {
                        handleError(data.data);
                    }
                })
                .catch(function(error) {
                    if (
                        error.response.data.status === 'error' &&
                      error.response.data.data.code ===
                          'verify_enrollment'
                    ) {
                        verifyEnrollment(error.response.data.data.action);
                    } else console.info(error);
                });
        };

        const getExpiryMonthYear = (dateString) => {
            const parts = dateString.split('-');
            return {
                'month': parts[1],
                'year': parts[0]
            };
        };

        const getCvcLengthBasedOnCardType = (cardType) => {
            const found = config.card_types.find(element => element.type.toLowerCase() === cardType.toLowerCase());
            if (found) {
                return found.cvc_length;
            }
            return [];
        };

        const handleClick = e => {
            submitForm();
            e.preventDefault();
        };

        const handleClickOnPayWithSavedPaymentMethod = e => {
            e.preventDefault();
            const processing = submitFormForPayingWithSavedCard();
            if (processing) {
                e.preventDefault();
            }
        };

        const handleChange = e => {
            const target = e.target;
            const value = target.checked;
            setSavePaymentMethodValue(value);
        };

        const handleOptionChange = e => {
            showCardForm(false);
            setSelectedPaymentMethod(e.target.value);
        };

        const handleChooseNewPaymentMethod = () => {
            showCardForm(true);
            setSelectedPaymentMethod(null);
        };

        const savedCards = savedPaymentMethods && savedPaymentMethods.length > 0 ? savedPaymentMethods.map((spm) => (
            <div className="wz-lyriapay-payline-savedpaymentmethod" key={spm.uuid}>
                <label>
                    <input type="radio" value={spm.uuid} checked={selectedPaymentMethod === spm.uuid} onChange={handleOptionChange} />
                    {spm.card_type} {spm.masked_number}
                </label>
                {selectedPaymentMethod === spm.uuid && (
                    <>
                        <div className="wz-lyriapay-payline-flexcontainer">
                            <ExpiryInput
                                readOnly={true}
                                onChange={c => setExpiry(c)}
                                expiryAsText={expiryAsText}
                                forceDirty={submitTried}
                                expiryMonthYear={getExpiryMonthYear(spm.expiry)}
                            />
                            <CvxInput
                                readOnly={isReadOnly}
                                onChange={c => setCvc(c)}
                                forceDirty={submitTried}
                                length={getCvcLengthBasedOnCardType(spm.payment_method)}
                            />
                        </div>
                        <button
                            className="wz-lyriapay-payline-formsubmit"
                            onClick={handleClickOnPayWithSavedPaymentMethod}
                            style={hidePayButton ? {
                                /*
                               note: we hide the button instead of removing it from the DOM
                               because the browser will trigger the buttonʼs onClick if we press Enter in any of the form input
                               it work if the button is hidden, it doesnʼt work if there is no button
                               */
                                display: 'none'
                            } : {}}
                        >
                            {t('payButton')}
                        </button>
                    </>
                )}
            </div>
        )): [];

        React.useImperativeHandle(ref, () => ({
            pay() {
                if (selectedPaymentMethod) {
                    return submitFormForPayingWithSavedCard();
                }
                return submitForm();
            }
        }));

        return (
            <div className="wz-lyriapay-payline">
                {isSpinnerVisible && (
                    <WaitingBlock loader={loader}>
                        {t('waitingPaymentResult')}
                    </WaitingBlock>
                )}
                {isFormVisible && (
                    <form noValidate>
                        {savedPaymentMethods.length > 0 && (
                            savedCards
                        )}
                        {savedPaymentMethods.length > 0 && !isCardFormVisible && (
                            <button
                                className="wz-lyriapay-payline-formsubmit"
                                onClick={handleChooseNewPaymentMethod}
                            >
                                {t('useNewPaymentMethod')}
                            </button>
                        )}
                        {(savedPaymentMethods.length === 0 || isCardFormVisible) && (
                            <>
                                <CardNumberInput
                                    readOnly={isReadOnly}
                                    onChange={c => setCard(c)}
                                    configCardTypes={config.card_types}
                                    forceDirty={submitTried}
                                />
                                <div className="wz-lyriapay-payline-flexcontainer">
                                    <ExpiryInput
                                        readOnly={isReadOnly}
                                        onChange={c => setExpiry(c)}
                                        expiryAsText={expiryAsText}
                                        forceDirty={submitTried}
                                    />
                                    <CvxInput
                                        readOnly={isReadOnly}
                                        onChange={c => setCvc(c)}
                                        forceDirty={submitTried}
                                        length={
                                            card.cardType
                                                ? card.cardType.cvc_length
                                                : []
                                        }
                                    />
                                </div>
                                {savePaymentMethod && selectedPaymentMethod === null && (
                                    <div className="wz-payline-savecard">
                                        <label>
                                            <input
                                                name="savepaymentmethod"
                                                value={savePaymentMethodValue}
                                                onChange={handleChange}
                                                type="checkbox"
                                            />
                                            {t('savePaymentMethod')}
                                        </label>
                                        <br/>
                                    </div>
                                )}
                                <button
                                    className="wz-lyriapay-payline-formsubmit"
                                    onClick={handleClick}
                                    style={hidePayButton ? {
                                        /*
                                      note: we hide the button instead of removing it from the DOM
                                      because the browser will trigger the buttonʼs onClick if we press Enter in any of the form input
                                      it work if the button is hidden, it doesnʼt work if there is no button
                                      */
                                        display: 'none'
                                    } : {}}
                                >
                                    {t('payButton')}
                                </button>
                            </>
                        )}
                    </form>
                )}
                <iframe
                    className="wz-lyriapay-iframe wz-lyriapay-payline-iframe-3ds"
                    title="iframe3ds"
                    ref={iframe3ds}
                    key={
                        `iframe3ds${attempts}` /* recreate a new iframe for each time we try the payment */
                    }
                    style={
                        isIframeShown ? {} : { display: 'none' }
                    }
                />
                <iframe
                    className="wz-lyriapay-payline-iframe-hidden"
                    title="iframe3dsmethod"
                    ref={iframe3dsmethod}
                    key={
                        `iframe3dsmethod${attempts}` /* recreate a new iframe for each time we try the payment */
                    }
                    style={{ display: 'none' }}
                />
            </div>
        );
    }
);

Payline.propTypes = {
    token: PropTypes.string.isRequired,
    lyriaUrl: PropTypes.string.isRequired,
    config: PropTypes.object,
    expiryAsText: PropTypes.bool,
    onPaymentStarted: PropTypes.func.isRequired,
    onPaymentResult: PropTypes.func.isRequired,
    hidePayButton: PropTypes.bool,
    loader: PropTypes.node,
    savePaymentMethod: PropTypes.bool,
    savedPaymentMethods: PropTypes.array,
    operation: PropTypes.string
};

export default Payline;
